import React, { useEffect } from 'react';
import styled from 'styled-components';
import LogoImageh18 from '~/assets/images/gmor-ai_logoh18.png';
import LogoSnsFb from '~/assets/images/sns_fb.png';
import LogoSnsX from '~/assets/images/sns_X.png';
import LogoSnsIn from '~/assets/images/sns_in.png';

type WindowWithProfile = Window & { ss_open_profile?: () => void };

const FooterWrapper = styled.div`
  position: relative;
  bottom: 0;
  width: 100%;
  border-top: 1px solid #d7d7d7;

  /* layout: stack parts vertically */
  display: flex;
  flex-direction: column;
  gap: 12px;

  .gmoask-footer-area {
    display: flex;
    align-items: center;
    width: 840px;
    margin: 0 auto;
    padding: 19px 10px;
    justify-content: flex-start;
  }

  .gmoask-footer-social-icons {
    display: flex;
    align-items: center;
    margin-left: 10px;
    gap: 8px;
  }

  .gmoask-footer-area img {
    height: 18px;
    width: auto;
  }

  #gmofootertag {
    display: block;
    max-width: 1040px;
    margin: 0 auto;
  }

  .security-seals {
    text-align: center;
    max-width: 1040px;
    margin: 0 auto;
  }

  #gmo-security-tag-area {
    display: block;
    max-width: 1040px;
    margin: 0 auto;
  }
`;

const GmoFooter: React.FC = () => {
  useEffect(() => {
    // GMO group footer script -----------------------------
    if (!document.getElementById('gmofootertag')) {
      const gmoFooter = document.createElement('script');
      gmoFooter.type = 'text/javascript';
      gmoFooter.charset = 'UTF-8';
      gmoFooter.src = 'https://cache.img.gmo.jp/gmo/footer/script.min.js';
      gmoFooter.id = 'gmofootertag';
      gmoFooter.async = true;
      gmoFooter.setAttribute('data-gmofooter-listed', 'true');
      const host = document.getElementById('gmo-group-footer-area') || document.body;
      host.appendChild(gmoFooter);
    }

    // GMO group security tag script -----------------------------
    if (!document.getElementById('gmosecuritytag')) {
      const gmoFooter = document.createElement('script');
      gmoFooter.type = 'text/javascript';
      gmoFooter.charset = 'UTF-8';
      gmoFooter.src = 'https://cache.img.gmo.jp/gmo/security/script.min.js';
      gmoFooter.id = 'gmosecuritytag';
      gmoFooter.async = true;
      const host = document.getElementById('gmo-security-tag-area') || document.body;
      host.appendChild(gmoFooter);
    }

    // GMO Security seals scripts ------------------------
    // GlobalSign
    const gsSeal = document.createElement('script');
    gsSeal.src = 'https://seal.atlas.globalsign.com/gss/one/seal?image=seal_100-50_ja.png';

    // GMO Cybersecurity siteseal
    const csiSeal = document.createElement('script');
    csiSeal.src = 'https://gmo-cybersecurity.com/siteseal/siteseal.js';
    csiSeal.defer = true;

    // Brand Security siteseal
    const brsSeal = document.createElement('script');
    brsSeal.src = 'https://seal.brandsecurity.gmo/static/js/siteseal.js';

    document.body.appendChild(gsSeal);
    document.body.appendChild(csiSeal);
    document.body.appendChild(brsSeal);

    // Cleanup to avoid duplicates on hot-reload/unmount
    return () => {
      [gsSeal, csiSeal, brsSeal].forEach((el) => {
        if (el && el.parentNode) el.parentNode.removeChild(el);
      });
    };
  }, []);

  return (
    <FooterWrapper>
      {/* GMOAsk common footer --------------------------------------------------- */}
      <div className="gmoask-footer-area">
        <div className="gmoask-footer-logo">
          <a
            href="https://gmo-research.ai/?__hstc=114247640.0eefcd03c1aa55fcfd234d63e8fa834c.1753768001844.1754573160328.1754618011653.6&__hssc=114247640.1.1754618011653&__hsfp=2803932446"
            target="_blank"
            rel="noopener noreferrer"
          >
            <img src={LogoImageh18} alt="GMO Research & AI" />
          </a>
        </div>

        <div className="gmoask-footer-social-icons">
          <a
            href="https://www.facebook.com/gmoresearchai/"
            target="_blank"
            rel="noopener noreferrer"
          >
            <img src={LogoSnsFb} alt="Facebook" />
          </a>
          <a href="https://x.com/GMOResearch_AI" target="_blank" rel="noopener noreferrer">
            <img src={LogoSnsX} alt="X" />
          </a>
          <a
            href="https://www.linkedin.com/company/gmo-research-ai"
            target="_blank"
            rel="noopener noreferrer"
          >
            <img src={LogoSnsIn} alt="LinkedIn" />
          </a>
        </div>
      </div>

      {/* GMO group security tags ------------------------------------------------ */}
      <div id="gmo-security-tag-area" />

      {/* GMO group security seals ------------------------------------------------ */}
      <div className="security-seals" style={{ textAlign: 'left' }}>
        <div style={{ display: 'inline-block' }}>
          <div
            id="ss_gmo_globalsign_secured_site_seal"
            onContextMenu={(e) => e.preventDefault()}
            style={{ width: 100, height: 50 }}
          >
            <button
              type="button"
              onClick={() => {
                (window as WindowWithProfile).ss_open_profile?.();
              }}
              style={{
                cursor: 'pointer',
                border: 0,
                padding: 0,
                background: 'none',
                width: '100%',
                height: '100%',
              }}
            >
              <img
                id="ss_gmo_globalsign_img"
                src="data:image/gif;base64,R0lGODlhAQABAGAAACH5BAEKAP8ALAAAAAABAAEAAAgEAP8FBAA7"
                alt="GMO GlobalSign seal"
                style={{ border: 0, width: '100%' }}
              />
            </button>
          </div>
        </div>

        <div style={{ display: 'inline-block', marginLeft: 20 }}>
          <span id="csi_siteseal_tag" onContextMenu={(e) => e.preventDefault()}>
            <a
              id="csi_siteseal_profile_link"
              href="https://gmo-cybersecurity.com/"
              target="_blank"
              rel="noopener noreferrer"
            >
              <img
                decoding="async"
                loading="lazy"
                alt="dark_typeA_100x50.png"
                id="csi_siteseal_image"
                width="100"
                height="50"
                src="#"
                style={{ display: 'none' }}
              />
            </a>
          </span>
        </div>

        <div style={{ display: 'inline-block', marginLeft: 20 }}>
          <span id="brs_siteseal_tag" onContextMenu={(e) => e.preventDefault()}>
            <a
              id="gmobrs_seal_profile_link"
              href="https://brandsecurity.gmo/"
              target="_blank"
              rel="noopener noreferrer"
            >
              <img
                decoding="async"
                alt="dark_typeA_100x50.png"
                id="gmobrs_seal_image"
                width="100"
                height="50"
                src="data:image/gif;base64,R0lGODlhAQABAGAAACH5BAEKAP8ALAAAAAABAAEAAAgEAP8FBAA7"
                style={{ display: 'none' }}
              />
            </a>
          </span>
        </div>
      </div>

      {/* GMO group footer ------------------------------------------------ */}
      <div id="gmo-group-footer-area" />
    </FooterWrapper>
  );
};

export default GmoFooter;
